'use client';

import React, { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import EnterpriseWaitlistModal from './EnterpriseWaitlistModal';

interface PricingTier {
  name: string;
  price: number;
  description: string;
  userLimit: string;
  features: string[];
  highlighted?: boolean;
  comingSoon?: boolean;
}

interface FeatureComparison {
  category: string;
  features: {
    name: string;
    basic: boolean | string;
    luxe: boolean | string;
    enterprise: boolean | string;
  }[];
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Basic plan',
    price: 20,
    description: 'Manual uploads only, no carrier sync',
    userLimit: '',
    features: [
      'Core commission tracking',
      'Manual CSV uploads (no carrier sync)',
      'Basic Payment Match reports',
      'Monthly data exports',
    ],
  },
  {
    name: 'Luxe plan',
    price: 40,
    description: 'Access to automation tools',
    userLimit: '',
    features: [
      'Everything in Basic',
      'Automated Carrier Connections',
      'Commission Tracker',
      'Automated Payment Match',
      'State-smart FMV rate detection',
      'Priority Revenue Recovery',
    ],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 0,
    description: 'Large Agencies',
    userLimit: '10+ Agents',
    features: ['Agency-Wide Overrides', 'Multi-Agent Management', 'Compliance Vault'],
  },
];

const featureComparison: FeatureComparison[] = [
  {
    category: 'Data Processing',
    features: [
      { name: 'Manual CSV Upload', basic: true, luxe: true, enterprise: true },
      { name: 'Weekly Smart Pulse Sync', basic: false, luxe: true, enterprise: true },
      { name: 'Real-time Carrier API Integration', basic: false, luxe: false, enterprise: true },
      { name: 'Automated Data Validation', basic: 'Basic', luxe: 'Advanced', enterprise: 'Advanced + Custom Rules' },
    ],
  },
  {
    category: 'Payment Match Tools',
    features: [
      { name: 'Commission Tracking', basic: true, luxe: true, enterprise: true },
      { name: 'Bank-Match Verification', basic: false, luxe: true, enterprise: true },
      { name: 'State-Smart FMV Rate Detection', basic: false, luxe: true, enterprise: true },
      { name: 'Priority Revenue Recovery', basic: false, luxe: true, enterprise: true },
      { name: 'Predictive Analytics', basic: false, luxe: false, enterprise: true },
    ],
  },
  {
    category: 'Team Management',
    features: [
      { name: 'Role-Based Access Control', basic: false, luxe: false, enterprise: true },
      { name: 'Audit Logs', basic: false, luxe: false, enterprise: true },
      { name: 'Team Performance Dashboard', basic: false, luxe: false, enterprise: true },
    ],
  },
];

export default function PricingInteractive() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const getPrice = (basePrice: number, tierName: string) => {
    if (billingCycle === 'annual') {
      if (tierName === 'Basic plan') return 180; // discounted annual for Basic
      if (tierName === 'Luxe plan') return 360; // discounted annual for Luxe
    }
    return basePrice;
  };

  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckIcon className="w-5 h-5 mx-auto" style={{ color: '#0f8958' }} />
      ) : (
        <XMarkIcon className="w-5 h-5 text-[#6B7280] mx-auto" />
      );
    }
    return <span className="font-body text-sm text-[#6B7280]">{value}</span>;
  };

  return (
    <div className="space-y-12">
      {/* Billing Cycle Toggle */}
      <div className="flex justify-center items-center gap-4">
        <span
          className={`font-body text-[15px] leading-tight ${
            billingCycle === 'monthly' ? 'text-[#236434] font-semibold' : 'text-[#6B7280]'
          }`}
        >
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
          className="relative w-12 h-5 bg-white rounded-full transition-colors hover:opacity-95"
        >
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform ${
              billingCycle === 'annual' ? 'translate-x-7' : ''
            }`}
            style={{ backgroundColor: '#236434' }}
          />
        </button>
        <span
          className={`font-body text-[15px] leading-tight ${
            billingCycle === 'annual' ? 'text-[#236434] font-semibold' : 'text-[#6B7280]'
          }`}
        >
          Annual <span className="text-[#6B7280]">($180 / $360)</span>
        </span>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-10">
        {pricingTiers.map((tier, index) => (
          <div
            key={`${tier.name}-${index}`}
            className={`relative rounded-xl border-0 p-8 transition-all shadow-sm ${
              tier.highlighted ? 'shadow-elevation-2 scale-105' : 'bg-white'
            }`}
            style={tier.highlighted ? { backgroundColor: '#236434' } : undefined}
          >
            {tier.highlighted && (
              <div
                className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-[6px] text-[15px] font-semibold font-body text-white leading-tight whitespace-nowrap"
                style={{ backgroundColor: '#0f8958' }}
              >
                Founder&apos;s Choice
              </div>
            )}

            {/* Card header */}
            <div className="pt-10 text-center mb-5">
              <h3
                className={`font-heading font-bold mb-1.5 leading-tight ${
                  tier.highlighted ? 'text-white' : 'text-[#236434]'
                }`}
                style={{ fontSize: '25px' }}
              >
                {tier.name}
              </h3>

              {tier.description && (
                <p
                  className={`font-body text-[15px] leading-tight mb-3 ${
                    tier.highlighted ? 'text-white/80' : 'text-[#6B7280]'
                  }`}
                >
                  {tier.description}
                </p>
              )}

              <div className="flex items-baseline justify-center gap-2">
                {tier.name === 'Enterprise' ? (
                  <span
                    className="font-bold font-heading leading-tight"
                    style={{ fontSize: '50px', color: '#0f8958' }}
                  >
                    Call
                  </span>
                ) : (
                  <>
                    <span
                      className={`font-bold font-heading leading-tight ${
                        tier.highlighted ? 'text-[#ffee80]' : ''
                      }`}
                      style={{
                        fontSize: '50px',
                        ...(!tier.highlighted ? { color: '#0f8958' } : {}),
                      }}
                    >
                      ${getPrice(tier.price, tier.name)}
                    </span>
                    {(tier.name !== 'Basic plan' && tier.name !== 'Luxe plan') || billingCycle === 'annual' ? (
                      <span
                        className={`font-body text-[15px] leading-tight ${
                          tier.highlighted ? 'text-white/80' : 'text-[#6B7280]'
                        }`}
                      >
                        /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    ) : null}
                  </>
                )}
              </div>

              {tier.name === 'Basic plan' || tier.name === 'Luxe plan' ? (
                <p
                  className={`font-body text-[15px] leading-tight mt-1.5 mb-2.5 ${
                    tier.highlighted ? 'text-white/80' : 'text-[#6B7280]'
                  }`}
                >
                  {billingCycle === 'monthly'
                    ? 'per user, per month'
                    : tier.name === 'Basic plan'
                    ? '$180 billed annually'
                    : '$360 billed annually'}
                </p>
              ) : tier.userLimit ? (
                <p
                  className={`font-body text-[15px] leading-tight mt-1.5 ${
                    tier.highlighted ? 'text-white/80' : 'text-[#6B7280]'
                  }`}
                >
                  {tier.userLimit}
                </p>
              ) : null}
            </div>

            {/* Features */}
            <ul className="space-y-2 mb-6">
              {tier.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckIcon
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{
                      color: tier.highlighted ? '#ffee80' : '#0f8958',
                      strokeWidth: '1.5px',
                      shapeRendering: 'geometricPrecision',
                    }}
                  />
                  <span
                    className={`font-body text-[15px] leading-tight ${
                      tier.highlighted ? 'text-white/90' : 'text-[#6B7280]'
                    }`}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              type="button"
              onClick={async () => {
                if (tier.name === 'Enterprise') {
                  setIsWaitlistModalOpen(true);
                  return;
                }
                if (tier.name === 'Basic plan' || tier.name === 'Luxe plan') {
                  const plan = tier.name === 'Luxe plan' ? 'Luxe' : 'Basic';
                  try {
                    const res = await fetch('/api/stripe/create-checkout-session', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ plan, interval: billingCycle }),
                    });
                    const data = await res.json();
                    if (res.status === 401) {
                      window.location.href = `/login-authentication?redirect=${encodeURIComponent('/public-subscription-selection')}`;
                      return;
                    }
                    if (data.url) window.location.href = data.url;
                    else if (data.error) alert(data.error);
                  } catch {
                    alert('Unable to start checkout. Please try again.');
                  }
                }
              }}
              className={`w-full py-3 mb-2.5 rounded-[6px] font-body text-[15px] font-semibold leading-tight transition-colors ${
                tier.highlighted
                  ? 'bg-[#ffee80] text-[#1a1a1a] hover:opacity-90'
                  : tier.name === 'Basic plan' || tier.name === 'Enterprise'
                  ? 'bg-[#236434] text-white hover:opacity-90'
                  : 'bg-white text-[#236434] hover:bg-[#f3f3f4]'
              }`}
            >
              {tier.name === 'Enterprise' ? 'Contact us' : 'Get early access'}
            </button>
          </div>
        ))}
      </div>

      {/* Feature Comparison Grid */}
      <div className="mt-32 md:mt-36">
        <h2 className="font-heading font-bold text-xl text-[#236434] text-center mb-8">
          Detailed Feature Comparison
        </h2>

        <div className="bg-white rounded-xl overflow-hidden">
          <div className="grid grid-cols-4 gap-2 md:gap-4 p-3 md:p-6 bg-white">
            <div className="font-body text-xs md:text-lg font-semibold text-[#236434]">
              Features
            </div>
            <div className="text-center font-body text-xs md:text-lg font-semibold text-[#236434]">
              Basic plan
            </div>
            <div className="text-center font-body text-xs md:text-lg font-semibold text-[#236434]">
              Luxe plan
            </div>
            <div className="text-center font-body text-xs md:text-lg font-semibold text-[#236434]">
              Enterprise
            </div>
          </div>

          {featureComparison.map((category) => (
            <div key={category.category}>
              <button
                onClick={() =>
                  setExpandedCategory(
                    expandedCategory === category.category ? null : category.category,
                  )
                }
                className="w-full p-6 text-left hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <h3 className="font-body text-base md:text-lg font-semibold text-[#236434]">
                  {category.category}
                </h3>
                <svg
                  className={`w-5 h-5 transition-transform ${
                    expandedCategory === category.category ? 'rotate-180' : ''
                  }`}
                  style={{ color: '#0f8958' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {expandedCategory === category.category && (
                <div className="bg-white">
                  {category.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-4 gap-2 md:gap-4 p-3 md:p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="font-body text-[10px] md:text-sm text-[#6B7280]">
                        {feature.name}
                      </div>
                      <div className="flex justify-center items-center">
                        {renderFeatureValue(feature.basic)}
                      </div>
                      <div className="flex justify-center items-center">
                        {renderFeatureValue(feature.luxe)}
                      </div>
                      <div className="flex justify-center items-center">
                        {renderFeatureValue(feature.enterprise)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <EnterpriseWaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
      />
    </div>
  );
}

